<script setup lang="ts">
import { ref, computed, getCurrentInstance, onUnmounted } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import Dropzone from '@/components/dropzone/Dropzone.vue';
import ScannerCredentialFields from '@/_core/entities/scanner-credential-fields/ui/ScannerCredentialFields.vue';
import {
  activateCode,
  getAvailableScans,
  payForScans,
} from '@/_core/entities/scan/api/scan.endpoints';
import { useFetchAvailableScans } from '@/_core/entities/scan/api/useFetchAvailableScans';
import { useFetchScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useFetchScannerCredentialFields';
import type { TDropzoneFile, TScanFile } from '@/_core/entities/scan/models/types';

type TStage = 'upload' | 'credentials' | 'activate';

const instance = getCurrentInstance();
const singleScanQueryPayload = computed(() => ({ mode: 'single-scan' as const }));
const { data: singleScanScannerCredentialFieldsData } =
  useFetchScannerCredentialFields(singleScanQueryPayload);

// Stage 1: Upload

const currentStage = ref<TStage>('upload');
const dropzoneRef = ref<{ removeFile: (file: TDropzoneFile) => void } | null>(null);
const allFiles = ref<TDropzoneFile[]>([]);
const isUploading = ref(false);
const showDropzone = ref(true);

const uploadedFiles = computed(() => allFiles.value.filter((f) => f.status === 'success'));
const trial = computed(() => instance?.proxy?.$store?.getters['user/getTrial']);
const singleScanScannerCredentialFields = computed(
  () => singleScanScannerCredentialFieldsData.value ?? []
);
const isFreeOrTrialScanFlow = computed(() => {
  if (!uploadedFiles.value.length) return false;

  return !!trial.value?.isTrial;
});
const shouldShowScannerCredentialFields = computed(
  () => uploadedFiles.value.length > 0 && !isFreeOrTrialScanFlow.value
);
const shouldUseScannerCredentialFieldsForScan = computed(() => {
  if (isFreeOrTrialScanFlow.value) return false;

  return singleScanScannerCredentialFields.value.some((field) => !!field?.value?.trim());
});

function onFilesAdded() {
  isUploading.value = true;
  showDropzone.value = false;
}

function onQueueComplete(files: TDropzoneFile[]) {
  const seen = new Set<string>();
  allFiles.value = files.filter((f) => {
    if (seen.has(f.name)) return false;
    seen.add(f.name);
    return true;
  });
  isUploading.value = false;
}

function removeFile(file: TDropzoneFile) {
  dropzoneRef.value?.removeFile(file);

  allFiles.value = allFiles.value.filter((f) => f.bucketKey !== file.bucketKey);

  if (allFiles.value.length !== 0) return;

  showDropzone.value = true;
}

function goToStage2() {
  if (shouldShowScannerCredentialFields.value) {
    currentStage.value = 'credentials';
    return;
  }

  currentStage.value = 'activate';
}

function goToStage3() {
  currentStage.value = 'activate';
}

function goBack() {
  if (currentStage.value === 'activate' && shouldShowScannerCredentialFields.value) {
    currentStage.value = 'credentials';
    return;
  }

  currentStage.value = 'upload';
}

// Stage 2: Activation & Scanning

const activationCodeInput = ref('');

const isCredentialsStage = computed(() => currentStage.value === 'credentials');
const isActivateStage = computed(() => currentStage.value === 'activate');
const { data: availableScansData, isLoading: isLoadingScans } =
  useFetchAvailableScans(isActivateStage);
const availableScansCount = computed(() => availableScansData.value ?? 0);

const canStartScanning = computed(
  () => uploadedFiles.value.length > 0 && availableScansCount.value >= uploadedFiles.value.length
);

const queryClient = useQueryClient();

let pollInterval: ReturnType<typeof setInterval> | null = null;

onUnmounted(() => {
  if (pollInterval !== null) clearInterval(pollInterval);
});

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_ATTEMPTS = 20;

const isRedemptionTimeoutError = ref(false);

const {
  mutate: redeemCode,
  isPending: isRedeemingCode,
  isError: isActivationError,
} = useMutation({
  mutationFn: (code: string) => activateCode(code),
  onMutate: () => {
    isRedemptionTimeoutError.value = false;
  },
  onSuccess: async () => {
    const initialCount = availableScansCount.value;

    let attempts = 0;
    const hasCountIncreased = await new Promise<boolean>((resolve) => {
      pollInterval = setInterval(async () => {
        attempts++;
        try {
          const count = await getAvailableScans();
          if (count > initialCount) {
            clearInterval(pollInterval!);
            pollInterval = null;
            resolve(true);
            return;
          }
        } catch {
          /* retry on next tick */
        }

        if (attempts >= MAX_POLLING_ATTEMPTS) {
          clearInterval(pollInterval!);
          pollInterval = null;
          resolve(false);
        }
      }, POLLING_INTERVAL_MS);
    });

    if (!hasCountIncreased) {
      isRedemptionTimeoutError.value = true;
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['availableScans'] });
  },
});

const {
  mutate: startScanning,
  isPending: isStartingScans,
  isSuccess: isScanSuccess,
  error: scanMutationError,
} = useMutation({
  mutationFn: async () => {
    const files: TScanFile[] = uploadedFiles.value.map((f) => ({
      sourceBucketKey: f.bucketKey,
      fileName: f.name,
      platform: f.platform as 'android' | 'ios',
      jobType: 'PREPAID',
    }));

    const result = await payForScans({
      files,
      isNotifyByEmail: true,
      isUsePrepaidScans: true,
      isUseScannerCredField: shouldUseScannerCredentialFieldsForScan.value,
      scanPackId: null,
      isNewCard: false,
    });

    if (!result.paid) throw new Error('Payment failed');

    return result;
  },
  onSuccess: () => {
    instance?.proxy.$router.push('/scans');
  },
});

const scanError = computed(() => {
  if (!scanMutationError.value) return '';
  const axiosError = scanMutationError.value as { response?: { data?: string } };
  return axiosError?.response?.data || 'An error occurred. Please try again.';
});
</script>

<template>
  <div class="tw:px-5 tw:py-4 tw:lg:px-20 tw:lg:py-8">
    <div class="tw:xl:w-240 tw:xl:mx-auto">
      <!-- Step indicator -->
      <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:sm:gap-3">
        <div class="tw:flex tw:items-center tw:gap-1.5 tw:min-w-0 tw:sm:gap-2">
          <div
            class="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:text-sm tw:font-bold tw:transition-colors"
            :class="
              currentStage === 'upload'
                ? 'tw:bg-main-blue tw:text-white'
                : 'tw:bg-blue-bg tw:text-main-darkblue'
            "
          >
            1
          </div>
          <span
            class="tw:hidden tw:sm:inline tw:whitespace-nowrap tw:text-sm tw:font-medium"
            :class="currentStage === 'upload' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
          >
            Upload Files
          </span>
          <span
            class="tw:inline tw:sm:hidden tw:whitespace-nowrap tw:text-xs tw:font-medium"
            :class="currentStage === 'upload' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
          >
            Upload
          </span>
        </div>
        <div class="tw:min-w-3 tw:flex-1 tw:h-px tw:bg-gray-200" />
        <template v-if="shouldShowScannerCredentialFields">
          <div class="tw:flex tw:items-center tw:gap-1.5 tw:min-w-0 tw:sm:gap-2">
            <div
              class="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:text-sm tw:font-bold tw:transition-colors"
              :class="
                currentStage === 'credentials'
                  ? 'tw:bg-main-blue tw:text-white'
                  : 'tw:bg-blue-bg tw:text-main-darkblue'
              "
            >
              2
            </div>
            <span
              class="tw:hidden tw:sm:inline tw:whitespace-nowrap tw:text-sm tw:font-medium"
              :class="currentStage === 'credentials' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
            >
              Login workflow credentials
            </span>
            <span
              class="tw:inline tw:sm:hidden tw:whitespace-nowrap tw:text-xs tw:font-medium"
              :class="currentStage === 'credentials' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
            >
              Login
            </span>
          </div>
          <div class="tw:min-w-3 tw:flex-1 tw:h-px tw:bg-gray-200" />
        </template>
        <div class="tw:flex tw:items-center tw:gap-1.5 tw:min-w-0 tw:sm:gap-2">
          <div
            class="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:text-sm tw:font-bold tw:transition-colors"
            :class="
              currentStage === 'activate'
                ? 'tw:bg-main-blue tw:text-white'
                : 'tw:bg-blue-bg tw:text-main-darkblue'
            "
          >
            {{ shouldShowScannerCredentialFields ? 3 : 2 }}
          </div>
          <span
            class="tw:hidden tw:sm:inline tw:whitespace-nowrap tw:text-sm tw:font-medium"
            :class="currentStage === 'activate' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
          >
            Activate &amp; Scan
          </span>
          <span
            class="tw:inline tw:sm:hidden tw:whitespace-nowrap tw:text-xs tw:font-medium"
            :class="currentStage === 'activate' ? 'tw:text-main-darkblue' : 'tw:text-ghost'"
          >
            Scan
          </span>
        </div>
      </div>

      <!-- Stage 1: Upload — v-show keeps Dropzone mounted so destroy() never fires -->
      <div v-show="currentStage === 'upload'" class="tw:mt-4">
        <div class="tw:typography-h1">Upload Files</div>

        <div v-if="uploadedFiles.length > 0" class="tw:mt-6">
          <div class="tw:typography-small tw:text-ghost">Uploaded files</div>
          <div class="tw:mt-2 tw:flex tw:flex-col tw:gap-2">
            <div
              v-for="file in uploadedFiles"
              :key="file.name"
              class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 tw:bg-blue-bg tw:rounded-lg"
            >
              <span class="tw:text-main-darkblue tw:text-sm">{{ file.name }}</span>
              <button
                class="tw:max-w-3.5 tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:text-ghost tw:hover:text-red-500 tw:transition-colors tw:rounded"
                type="button"
                :aria-label="`Remove ${file.name}`"
                @click="removeFile(file)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M1 1L11 11M11 1L1 11"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div v-show="showDropzone && !isUploading" class="tw:mt-4">
          <Dropzone
            ref="dropzoneRef"
            @files-added="onFilesAdded"
            @queue-complete="onQueueComplete"
          />
        </div>

        <div
          v-if="isUploading"
          class="tw:min-h-40 tw:mt-4 tw:flex tw:justify-center tw:items-center tw:gap-2 tw:text-ghost tw:typography-h2"
        >
          <span class="upload-spinner" />
          Uploading...
        </div>

        <button
          v-if="!showDropzone && !isUploading"
          class="tw:mt-4! btn-secondary"
          type="button"
          @click="showDropzone = true"
        >
          Add another file
        </button>

        <div class="tw:mt-8 tw:flex tw:justify-end">
          <button
            class="btn-primary"
            :disabled="uploadedFiles.length === 0 || isUploading"
            type="button"
            @click="goToStage2"
          >
            Next
          </button>
        </div>
      </div>

      <!-- Stage 2: Login workflow credentials -->
      <div v-show="isCredentialsStage" class="tw:mt-4">
        <div class="tw:typography-h1">Login workflow credentials</div>

        <ScannerCredentialFields
          class="tw:mt-4"
          :hide-title="true"
          mode="single-scan"
          title="Login workflow credentials"
        />

        <div class="tw:mt-4 tw:flex tw:items-center tw:justify-end tw:gap-2">
          <button class="btn-secondary tw:min-w-35" type="button" @click="goBack">Back</button>
          <button class="btn-primary" type="button" @click="goToStage3">Next</button>
        </div>
      </div>

      <!-- Stage 3: Activation & Scanning -->
      <div v-show="currentStage === 'activate'" class="tw:mt-4">
        <div class="tw:typography-h1">
          {{ canStartScanning ? 'Start scanning' : 'Redeem a code' }}
        </div>

        <div
          v-if="isLoadingScans"
          class="tw:flex tw:text-secondary tw:justify-center! tw:items-center tw:min-h-60"
        >
          Loading...
        </div>

        <template v-else>
          <div
            v-if="!canStartScanning"
            class="tw:mt-4 tw:p-6 tw:bg-blue-bg tw:rounded-xl tw:flex tw:flex-col tw:gap-4"
          >
            <div class="tw:text-main-darkblue">Enter an activation code to unlock scans.</div>
            <div class="tw:flex tw:flex-col tw:gap-1.5">
              <label class="tw:typography-small tw:text-ghost" for="activation-code">
                Activation Code
              </label>
              <input
                id="activation-code"
                v-model="activationCodeInput"
                type="text"
                class="tw:px-4 tw:py-2.5 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:text-main-darkblue tw:text-sm tw:outline-none tw:transition-colors focus:tw:border-main-blue"
                placeholder="Enter your activation code"
              />
            </div>
            <div v-if="isActivationError" class="tw:text-red-500 tw:text-sm">
              Invalid code. Please double-check and try again.
            </div>
            <div v-if="isRedemptionTimeoutError" class="tw:text-red-500 tw:text-sm">
              Code was accepted but scan count did not update. Please refresh and try again.
            </div>
            <div>
              <button
                class="btn-primary"
                :disabled="isRedeemingCode || !activationCodeInput.trim()"
                type="button"
                @click="() => redeemCode(activationCodeInput)"
              >
                {{ isRedeemingCode ? 'Redeeming...' : 'Redeem code' }}
              </button>
            </div>
          </div>

          <div
            class="tw:mt-4 tw:flex tw:justify-end tw:text-main-darkblue"
            :class="{
              'tw:justify-center! tw:items-center tw:min-h-60': canStartScanning,
            }"
          >
            You have {{ availableScansCount }} available scan{{
              availableScansCount === 1 ? '' : 's'
            }}.
            <span v-if="canStartScanning">You're ready to scan.</span>
          </div>

          <div v-if="scanError" class="tw:text-red-500 tw:text-sm">
            {{ scanError }}
          </div>

          <div class="tw:mt-4 tw:flex tw:items-center tw:justify-end tw:gap-2">
            <button class="btn-secondary tw:min-w-35" type="button" @click="goBack">Back</button>
            <button
              class="btn-primary"
              :disabled="!canStartScanning || isStartingScans"
              type="button"
              @click="() => startScanning()"
            >
              <!-- isScanSuccess condition is here because there's a delay between the router navigation -->
              {{ isStartingScans || isScanSuccess ? 'Starting...' : 'Start scanning' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.upload-spinner {
  display: inline-block;
  width: 36px;
  height: 36px;
  border: 5px solid #adccfb;
  border-top-color: #021631;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-primary {
  padding: 12px 32px;
  background-color: #021631;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.btn-secondary {
  padding: 12px 24px;
  background-color: transparent;
  color: #706969;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s;

  &:hover {
    color: #021631;
    border-color: #021631;
  }
}
</style>
